<script setup lang="ts">
import type { BasicUser } from '~/models/User/BasicUser'
import type { FlytTestAssignment, TestAnswer, TestData, TestResult } from '~/models/Assignment'
import { computed } from 'vue'
import { faChevronDown } from '@fortawesome/duotone-regular-svg-icons'
import ResultGraph from '~/components/reports/ResultGraph.vue'
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from 'reka-ui'
import { PendoFeature } from '~/models/Pendo'

const props = defineProps<{
  reportData: FlytTestAssignment[]
  user: BasicUser
}>()

const testData = computed((): TestResult[] => {
  const formatedData = props.reportData
    .filter(
      assignment =>
        assignment.responseSummary.some(
          response => response.student?.userId === props.user.userId
        )
    )
    .map(assignment => {
      const data = parseJson(assignment.responseSummary.find(summary => summary.student.userId === props.user.userId)?.latestAnswer?.data) as TestData

      let correctAnswers = 0
      let wrongAnswers = 0
      let unansweredAnswers = 0

      data.items?.forEach(item => {
        correctAnswers += item.score.correctAnswers
        wrongAnswers += item.score.totalQuestions - item.score.correctAnswers
      })

      return {
        user: props.user,
        assignmentKey: assignment.assignment.assignmentKey,
        name: assignment.assignment.name,
        correct: correctAnswers,
        wrong: wrongAnswers,
        unanswered: unansweredAnswers,
      }
    })

  return formatedData.sort((a, b) => {
    const numA = parseInt(a.name.split(' ')[0], 10)
    const numB = parseInt(b.name.split(' ')[0], 10)
    return numA - numB
  })
})

const studentData = computed((): TestResult => {
  const userSummaries = props.reportData
    .flatMap(assignment => assignment.responseSummary)
    .filter(summary => summary.student.userId === props.user.userId)

  const dataFields = userSummaries.flatMap(summary => {
    return summary.latestAnswer?.data
  })

  const groupedItems = getGroupedItems()

  const items = dataFields
    .filter(answer => answer !== undefined)
    .map(answer => JSON.parse(answer))
    .flatMap(data => data.items)

  let correctAnswers = 0
  let wrongAnswers = 0


  Object.keys(groupedItems).forEach(key => {
    const item = items.find(item => item.itemId === key)

    if (item) {
      correctAnswers += item.score.correctAnswers
      wrongAnswers += item.score.totalQuestions - item.score.correctAnswers
    }
  })

  return {
    user: props.user,
    correct: correctAnswers,
    wrong: wrongAnswers,
    unanswered: 0
  }
})

const getGroupedItems = () => {
  const dataFields = props.reportData.flatMap(assignment => {
    return assignment.responseSummary.flatMap(summary => {
      return summary.latestAnswer?.data
    })
  })

  const items: TestAnswer[] = dataFields
    .filter(answer => answer !== undefined)
    .map(answer => JSON.parse(answer))
    .flatMap(data => {
      return data.items
    })

  return items.reduce((acc, item) => {
    if (!acc[item.itemId]) {
      acc[item.itemId] = []
    }
    acc[item.itemId].push(item)
    return acc
  }, {} as { [key: string]: TestAnswer[] })
}

const studentFractionScore = computed(() => {
  const correct = studentData.value.correct ?? 0
  const wrong = studentData.value.wrong ?? 0
  const total = correct + wrong

  return `${correct}/${total}`
})

const parseJson = (data?: string): Nullable<object> => {
  if (!data) return null
  try {
    const json = JSON.parse(data)
    return (json instanceof Object) ? <object>json : null
  } catch {
    return null
  }
}
</script>

<template>
  <AccordionRoot
    class="w-full"
    type="single"
    :collapsible="true"
    :data-pendo="PendoFeature.Report.ToggleAccordion"
  >
    <AccordionItem
      :value="props.user.userId"
    >
      <AccordionHeader>
        <AccordionTrigger class="w-full group focus-visible:ring hover:bg-green-10/20">
          <div class="mx-auto max-w-screen-au">
            <div class="h-14 flex flex-row">
              <div class="content-center basis-6/12">
                <p class="px-4 text-xl font-medium text-left">
                  {{ props.user.fullName }}
                </p>

              </div>
              <div
                ref="wrapper"
                class="flex basis-5/12"
              >
                <p
                  ref="part"
                  class="content-center pr-4 text-3xl font-medium"
                  v-text="`${studentFractionScore}`"
                />
                <ResultGraph class="w-full" :test-result="studentData" />
              </div>
              <font-awesome-icon class="basis-1/12 transition-transform duration-300 group-data-[state=open]:rotate-180 h-1/2 self-center text-seagreen-50" :icon="faChevronDown" />
            </div>
          </div>
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent class="data-[state=open]:animate-accordion-open data-[state=closed]:animate-accordion-close overflow-hidden p-2">
        <ul>
          <li
            v-for="testResult in testData"
            :key="testResult.assignmentKey"
          >
            <div class="mx-auto max-w-screen-au">
              <div class="grid h-14 xs:grid-cols-2">
                <div class="content-center py-4 col-start-2">
                  <p v-text="`${testResult.name} ${testResult.correct}/${testResult.correct + testResult.wrong}`"></p>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </AccordionContent>
    </AccordionItem>
  </AccordionRoot>
</template>
